import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import Sources from '../../components/Sources';
import FAQSection from '../../components/FAQSection';
import TableOfContents from '../../components/TableOfContents';
import Callout from '../../components/Callout';
import Breadcrumbs from '../../components/Breadcrumbs';
import { LightsDiagram } from '../../components/Diagrams';

export default function TrailerLysRegler() {
  const breadcrumbs = [
    { name: "Forside", path: "/" },
    { name: "Syn og udstyr", path: "/trailer-syn" },
    { name: "Lygter og reflekser", path: "/trailer-lys-regler" }
  ];
  const tocItems = [
    { id: "lovpligtige-lygter-bagpaa", title: "Lovpligtige lygter og reflekser bagpå" },
    { id: "trekantede-reflekser", title: "Kravet om røde trekantreflekser" },
    { id: "positionslygter-og-sidelys", title: "Positionslygter foran og sidemarkeringer" },
    { id: "7-pol-vs-13-pol", title: "Trailerstik: 7-polet vs. 13-polet stik" },
    { id: "typiske-fejl-og-fejlfinding", title: "De mest almindelige fejl og fejlsøgning" }
  ];

  const faq = [
    {
      q: "Hvorfor skal reflekserne på en trailer være trekantede?",
      a: "I færdselsloven er røde trekantede reflekser med spidsen opad forbeholdt påhængskøretøjer. De advarer bagvedkørende bilister i mørke om, at der er tale om et vogntog med anhænger, som accelererer langsommere og har længere bremselængde."
    },
    {
      q: "Hvornår skal en trailer have hvide positionslygter foran?",
      a: "Hvis traileren er bredere end 1,60 meter, eller hvis den rager mere end 40 cm ud over trækbilens forlygter/positionslygter, skal den være udstyret med to fremadrettede hvide positionslygter."
    },
    {
      q: "Er baklys lovpligtigt på en trailer?",
      a: "Baklys er kun et lovkrav på nyere trailere med en tilladt totalvægt over visse vægtklasser godkendt efter EU-krav. På almindelige mindre trailere med 7-polet stik er baklys ikke obligatorisk, men på nyere trailere med 13-polet stik er det ofte standardmonteret."
    },
    {
      q: "Hvorfor blinker trailerens lygter svagt eller uregelmæssigt, når jeg bremser?",
      a: "Dette skyldes næsten altid en dårlig stelforbindelse (benspor 3 i et 7-polet stik). Når stelkablet er oxideret eller løst, søger strømmen retur gennem de andre pærer (f.eks. blinklys og positionslys), hvilket får alle pærer til at lyse svagt samtidig."
    }
  ];

  const sources = [
    { title: "Bekendtgørelse om køretøjers indretning og udstyr (BEK nr. 154 af 28/01/2021)", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2021/154" },
    { title: "Færdselsloven § 31 og § 32 om lygteføring (LBK nr. 1324 af 18/09/2023)", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2023/1324" }
  ];

  return (
    <>
      <SEO 
        title="Trailer lys regler: Lovpligtige lygter, reflekser og stik" 
        description="Få styr på lygteregler for trailer: baglygter, stoplys, blink, de lovpligtige røde trekantreflekser, 7- og 13-polede stik og fejlfinding." 
        pathname="/trailer-lys-regler"
        faq={faq}
        breadcrumbs={breadcrumbs}
      />

      <article>
        <Breadcrumbs items={breadcrumbs} />
        <h1 className="text-[30px] sm:text-[36px] font-bold text-[#2E3A46] tracking-tight leading-tight mb-4">
          Lygter og reflekser på trailer: Regler og kontrol
        </h1>

        <p className="text-[18px] text-[#1A1C1F] leading-relaxed mb-6 font-medium">
          En trailer skal bagpå altid have <strong>to røde baglygter</strong>, <strong>to røde stoplygter</strong>, <strong>to gule blinklygter</strong>, <strong>mindst én hvid nummerpladelygte</strong> samt <strong>to røde trekantede reflekser</strong> med spidsen opad.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Er traileren bredere end 1,60 meter, skal der desuden være hvide positionslygter foran. På siderne skal der være gule reflekser. Alle lygter skal fungere fejlfrit – defekte lygter er den hyppigste årsag til anmærkninger ved syn og politistop.
        </p>

        <TableOfContents items={tocItems} />

        <LightsDiagram caption="Figur 1: Lovpligtig placering af lygter og reflekser set bagfra på en trailer." />

        <Callout title="Tjekliste før hver køretur">
          <ul className="m-0 p-0 list-none space-y-1 font-mono text-[15px]">
            <li>1. Baglygter lyser rødt i begge sider.</li>
            <li>2. Stoplygter tænder kraftigt ved tryk på bremsen.</li>
            <li>3. Begge gule blinklygter blinker i takt med bilen.</li>
            <li>4. Nummerpladelygten oplyser hele nummerpladen.</li>
            <li>5. De to røde trekantreflekser er hele og rene.</li>
          </ul>
        </Callout>

        <h2 id="lovpligtige-lygter-bagpaa" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Lovpligtige lygter og reflekser bagpå
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          I henhold til Detailforskrifter for Køretøjer gælder følgende minimumskrav for bagenden af alle trailere:
        </p>

        <div className="my-6 overflow-x-auto border-t border-[#B58200]">
          <table className="w-full text-left text-[15px] border-collapse">
            <thead>
              <tr className="border-b border-[#E4E1D8] font-mono text-[13px] uppercase text-[#2E3A46]">
                <th className="py-2.5 pr-4 font-semibold">Lygte / Refleks</th>
                <th className="py-2.5 pr-4 font-semibold">Antal &amp; Farve</th>
                <th className="py-2.5 font-semibold">Placeringskrav</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E1D8] tabular-nums">
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Baglygter</td>
                <td className="py-3 pr-4">2 stk. rød</td>
                <td className="py-3 text-[#5C636B]">Højst 40 cm fra yderkant, mindst 35 cm over vej</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Stoplygter (bremselys)</td>
                <td className="py-3 pr-4">2 stk. rød</td>
                <td className="py-3 text-[#5C636B]">Skal lyse væsentligt kraftigere end baglygter</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Blinklygter (retningsvisere)</td>
                <td className="py-3 pr-4">2 stk. gul / rav</td>
                <td className="py-3 text-[#5C636B]">Yderst i lygtekonsollen</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Nummerpladelygte</td>
                <td className="py-3 pr-4">1 eller 2 stk. hvid</td>
                <td className="py-3 text-[#5C636B]">Skal oplyse nummerpladen uden at blænde bagud</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Bagreflekser</td>
                <td className="py-3 pr-4 font-bold text-[#B58200]">2 stk. rød (trekantede)</td>
                <td className="py-3 text-[#5C636B]">Spidsen skal pege opad, højst 40 cm fra yderkant</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2 id="trekantede-reflekser" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Kravet om røde trekantreflekser
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Et af de mest oversete krav er de <strong>trekantede reflekser</strong>. Mange køber uoriginale firkantede eller runde reflekser, når de gamle knækker – men dette er ulovligt.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          I europæisk færdselslovgivning må trekantede reflekser udelukkende monteres på påhængskøretøjer. De skal vende med <strong>spidsen pegende lodret opad</strong>. Hvis en trailer fremstilles til syn med runde reflekser bagpå, kasseres den omgående.
        </p>

        <h2 id="positionslygter-og-sidelys" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Positionslygter foran og sidemarkeringer
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Afhængigt af trailerens fysiske mål gælder der ekstra belysningskrav:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li><strong>Bredde over 1,60 m:</strong> Traileren <em>skal</em> have to fremadrettede hvide positionslygter monteret forrest på kassen.</li>
          <li><strong>Bredde over 2,10 m:</strong> Skal have fremadrettede hvide og bagudrettede røde markeringstornlygter (gummiarme).</li>
          <li><strong>Længde over 6,00 m:</strong> Skal have gule sidemarkeringslygter med jævne mellemrum langs trailerens sidevanger.</li>
          <li><strong>Forreflekser:</strong> Alle trailere skal have 2 hvide ikke-trekantede reflekser foran.</li>
          <li><strong>Sidereflekser:</strong> Mindst 1 gul refleks på hver side (for trailere under 6 meter).</li>
        </ul>

        <h2 id="7-pol-vs-13-pol" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Trailerstik: 7-polet vs. 13-polet stik
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          I dag anvendes der to stikstandarder mellem trækbil og trailer:
        </p>

        <div className="my-6 p-4 border border-[#E4E1D8] bg-[#FBFAF6] rounded-[4px] space-y-3">
          <div>
            <h3 className="text-[16px] font-bold text-[#2E3A46] mb-1 font-mono uppercase">
              7-polet stik (ISO 1724)
            </h3>
            <p className="text-[15px] text-[#1A1C1F] m-0">
              Den ældre standard. Leverer strøm til venstre/højre positionslys, blinklys, stoplys, tågebaglys og stel. Har ikke forbindelse til baklys eller konstant strøm til batteri/køleskab.
            </p>
          </div>
          <div className="border-t border-[#E4E1D8] pt-3">
            <h3 className="text-[16px] font-bold text-[#2E3A46] mb-1 font-mono uppercase">
              13-polet stik (ISO 11446 - Jaeger)
            </h3>
            <p className="text-[15px] text-[#1A1C1F] m-0">
              Den moderne bajonetstandard. Giver separat stel, aktivt baklys, tågebaglys med automatisk afbrydelse af bilens tågebaglys samt konstant 12V strøm og tændingsstyret ladeledning til batteri.
            </p>
          </div>
        </div>

        <h2 id="typiske-fejl-og-fejlfinding" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          De mest almindelige fejl og fejlsøgning
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Oplever du lysproblemer, skyldes det i 9 ud af 10 tilfælde et af følgende punkter:
        </p>

        <ol className="list-decimal list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li><strong>Dårlig stelforbindelse:</strong> Hvis lygterne blinker i kryds eller lyser svagt, er stelledningen i stikket (ben 3) eller stelskruen i trailerens lygtekonsol irret eller løs.</li>
          <li><strong>Ir i stikket:</strong> Fugt i 7- eller 13-polet stik skaber kortslutninger mellem polerne. Rens med kontaktspray og en lille messingbørste.</li>
          <li><strong>Knækkede pærer:</strong> Vibrationer fra trailerens stive affjedring slår ofte glødetråde i stykker. Overvej udskiftning til godkendte LED-trailerlygter (kræver modstand, hvis bilen melder pærefejl).</li>
        </ol>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Læs også om kravene ved syn i guiden <Link to="/trailer-syn" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">syn af trailer</Link> og nummerplademontering under <Link to="/trailer-nummerplade" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailer nummerplade</Link>.
        </p>

        <FAQSection items={faq} />
        <Sources links={sources} />
      </article>
    </>
  );
}
